import {
  useOrchidDriveUploadTasks,
  usePatientChartsDrive,
} from "contexts/orchid-drive";
import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
// types
import type {
  DataTransferImportTask,
  DataTransferUploadTask,
  DataTransferUploadTaskState,
} from "../types";
import { FileEntry } from "contexts/orchid-drive/types";

type ContextProps = {
  uploadTaskStates: DataTransferUploadTaskState[];
  importTasks: DataTransferImportTask[];
  onUpload: (arg: File[], source: string) => Promise<DataTransferUploadTask[]>;
  onAbort: (arg: DataTransferUploadTaskState) => Promise<void>;
  onHideUploader: () => void;
  // TODO: update the type here
  onSubmitPassword: () => void;
};

const DataTransferContext = createContext<ContextProps>({
  uploadTaskStates: [],
  importTasks: [],
  onUpload: () => Promise.resolve([]),
  onAbort: () => Promise.resolve(),
  onHideUploader: () => null,
  onSubmitPassword: () => null,
});

type Props = {
  children: ReactNode;
};

/**
 * create context to be consumed in data transfer component
 */
const DataTransferContextProvider = ({ children }: Props) => {
  const { uploadTasks } = useOrchidDriveUploadTasks();

  // TODO: should change to another drive here
  const { uploadFile, uploadTaskAbort } = usePatientChartsDrive({
    patientCharts: [],
  });
  const [uploadTasksInView, setUploadTasksInView] = useState<
    { uploadTaskId: string; name: string; source: string }[]
  >([]);
  const [importTasks, setImportTasks] = useState<ContextProps["importTasks"]>(
    [],
  );

  // handle upload, it should cover all cases (drag / input /...)
  const onUpload = useCallback<ContextProps["onUpload"]>(
    async (files, source) => {
      const tasks: DataTransferUploadTask[] = [];

      await Promise.allSettled(
        files.map(async (file) => {
          try {
            const uploadTask = await uploadFile({
              storageDrive: "patient-chart",
              patientUserId: "567bf9f4-6f82-49d1-9ed1-bbbe4cafbe14",
              files: [
                {
                  file: new FileEntry(file, { name: file.name }),
                  baseDirectoryId: null,
                },
              ],
            });

            if (uploadTask) {
              setUploadTasksInView((s) => [
                ...s,
                {
                  uploadTaskId: uploadTask.uploadTaskId,
                  name: file.name,
                  // TODO: remove this line, it should be returned from BE
                  source,
                },
              ]);
              tasks.push({
                uploadTaskId: uploadTask.uploadTaskId,
                name: file.name,
                size: file.size,
                source,
              });
            } else {
              throw new Error("upload task is not created");
            }
          } catch (error) {
            console.error("upload task is not created");
          }
        }),
      );
      return tasks;
    },
    [uploadFile],
  );

  /**
   * on close the upload modal
   */
  const onHideUploader = useCallback(() => {
    const nextImportTasks = uploadTasks.reduce((prev, el) => {
      if (el.uploadTask) {
        const task = uploadTasksInView.find(
          (item) => item.uploadTaskId === el.uploadTask?.uploadTaskId,
        );
        if (task && el.uploadTask.uploadPercent === 100) {
          prev.push({
            id: task.uploadTaskId,
            source: "simplePractice",
            name:
              task.name || el.uploadTask.files.map((el) => el.name).join(" "),
            status: "processing",
            // TODO: should use uploadMarkedFinalizedDatetime???
            createDatetime: el.uploadTask.uploadFinalizedDatetime as Date,
          });
        }
      }

      return prev;
    }, [] as ContextProps["importTasks"]);

    setImportTasks((s) => [...s, ...nextImportTasks]);

    // reset
    setUploadTasksInView([]);
  }, [uploadTasks, uploadTasksInView]);

  // mange the task rows in the upload modal
  const uploadTaskStates = useMemo(() => {
    const res = uploadTasks.reduce((prev, el) => {
      if (el.uploadTask) {
        const task = uploadTasksInView.find(
          (item) => item.uploadTaskId === el.uploadTask?.uploadTaskId,
        );
        if (task) {
          prev.push({
            name:
              task.name || el.uploadTask.files.map((el) => el.name).join(" "),
            id: el.uploadTask.uploadTaskId,
            percent: el.uploadTask.uploadPercent ?? 0,
            // TODO: update the field, it should be returned from backend
            requirePassword: false,
          });
        }
      }

      return prev;
    }, [] as ContextProps["uploadTaskStates"]);

    return res;
  }, [uploadTasks, uploadTasksInView]);

  // on abort come task in upload modal
  const onAbort = useCallback<ContextProps["onAbort"]>(
    async (arg) => {
      try {
        await uploadTaskAbort({ uploadTaskId: arg.id });
        setUploadTasksInView((s) => {
          return s.filter((el) => el.uploadTaskId !== arg.id);
        });
      } catch (error) {
        console.error(error);
      }
    },
    [uploadTaskAbort],
  );

  // TODO: update the fn on submit password for one file
  const onSubmitPassword = useCallback(() => {}, []);

  return (
    <DataTransferContext.Provider
      value={{
        uploadTaskStates,
        importTasks,
        onUpload,
        onAbort,
        onHideUploader,
        onSubmitPassword,
      }}
    >
      {children}
    </DataTransferContext.Provider>
  );
};

export default DataTransferContextProvider;

export const useDataTransferContext = () => useContext(DataTransferContext);
